import { useGlobalModal } from '@/hooks/useGlobalModal';
import GlobalModal from '@components/GlobalModal';
import { Access } from '@features/authorization';
import { msg, t } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import {
  Button,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogContentText,
  Typography,
} from '@mui/material';
import { type FC, useState } from 'react';
import { useMutation } from '@apollo/client';
import TRIGGER_MASSIVE_DGA_RETRY from '@features/dga/graphql/mutations/triggerRetryForAllDevices';
import { useModalStatusMessage } from '@/hooks';

const MassiveDGARetryModal: FC = () => {
  const { _ } = useLingui();

  const { globalModalProps, state, onClose } = useGlobalModal('massiveDGARetryModal', {
    closeConfirmation: true,
  });

  const [retryMassive, { loading }] = useMutation(TRIGGER_MASSIVE_DGA_RETRY);
  const [confirmMode, setConfirmMode] = useState(false);
  const { openModalErrorMessage, openModalSuccessMessage } = useModalStatusMessage();

  const handleStartRetry = () => {
    setConfirmMode(true);
  };

  const handleCancel = () => {
    setConfirmMode(false);
  };

  const handleConfirmRetry = async () => {
    try {
      const response = await retryMassive();
      if (response?.data?.triggerRetryForAllDevices?.success) {
        setConfirmMode(false);
        onClose();
        openModalSuccessMessage(_(msg`Se inició el proceso de reintento masivo exitosamente.`));
      } else {
        throw Error();
      }
    } catch {
      openModalErrorMessage(
        _(msg`Ocurrió un error al intentar el reenvío masivo. Por favor intenta nuevamente.`),
      );
    }
  };

  return (
    <GlobalModal
      {...globalModalProps}
      dialogProps={{ maxWidth: 'sm' }}
      modalTitleProps={{
        title: t`Reintento masivo de datos DGA`,
      }}
      authorization={{
        access: Access.RetryMassiveDGASubmissions,
        onClose,
      }}
    >
      {state.open && (
        <>
          <DialogContent>
            {!confirmMode ? (
              <>
                <DialogContentText color="error" sx={{ mb: 3, fontWeight: 500 }}>
                  {t`¿Desea iniciar el reintento masivo de todos los registros fallidos?`}
                </DialogContentText>

                <Typography variant="body2" color="text.secondary" paragraph>
                  {t`Esta acción intentará reenviar todos los reportes que fallaron anteriormente. El proceso puede tardar varios minutos dependiendo de la cantidad de registros.`}
                </Typography>
              </>
            ) : (
              <>
                <DialogContentText sx={{ mb: 3, fontWeight: 500 }}>
                  {t`¿Está seguro que desea continuar?`}
                </DialogContentText>

                <DialogContentText color="error">
                  {t`Esta acción no se puede deshacer y generará nuevos intentos de envío para todos los registros fallidos.`}
                </DialogContentText>
              </>
            )}
          </DialogContent>

          <DialogActions>
            {!confirmMode ? (
              <>
                <Button onClick={() => onClose()} disabled={loading}>
                  {t`Cancelar`}
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleStartRetry}
                  disabled={loading}
                >
                  {t`Iniciar reintento masivo`}
                </Button>
              </>
            ) : (
              <>
                <Button onClick={handleCancel} disabled={loading} variant="outlined">
                  {t`Volver`}
                </Button>

                <Button
                  variant="contained"
                  color="error"
                  onClick={handleConfirmRetry}
                  disabled={loading}
                  startIcon={loading && <CircularProgress size={20} color="inherit" />}
                >
                  {loading ? t`Procesando...` : t`Confirmar y reintentar ahora`}
                </Button>
              </>
            )}
          </DialogActions>
        </>
      )}
    </GlobalModal>
  );
};

export default MassiveDGARetryModal;
